import { coerceToDate, formatDateAsIsoLocal } from './date';

/**
 * One shared formatter for showing date-times in the signed-in user's time zone
 * (WI 35959, child 4 of #35260).
 *
 * Two kinds of value, two functions, never mixed:
 *
 * - **Instants** (a moment in time: comment created, activity logged, record
 *   modified) go through {@link formatUserDateTime}. The value is rendered in the
 *   viewer's zone, so one instant shows 10:30 PM to a Central viewer and 8:30 PM
 *   to a Pacific viewer.
 * - **Calendar dates** (no time: bid due date, Salesforce task due date) go
 *   through {@link formatCalendarDate} / {@link toCalendarDateIso}. They are never
 *   shifted by a time zone, so Oct 15 stays Oct 15 for every viewer.
 *
 * No offset math is done on values without a zone. An ISO string with `Z` or an
 * offset is an exact instant. One without (the wire format of a legacy
 * Central-wall-time column) is read as local time, exactly as
 * `HttpReviveDatesInterceptor` and Angular's `DatePipe` already read it, so
 * today's no-Z data renders as it does now and the server-side move to `Z`
 * (#35956, #35957, #35958) makes it correct for every zone with no client change.
 */

/**
 * `short`: 10/15/26, 10:30 PM. `medium`: Oct 15, 2026, 10:30:00 PM. `numeric`: 10/15/2026 10:30 PM.
 * `numericDate`: 10/15/2026, the calendar day the instant falls on in the zone.
 */
export type UserDateTimeStyle = 'short' | 'medium' | 'numeric' | 'numericDate';

/** Central (company) time, daylight saving included. */
export const CENTRAL_TIME_ZONE = 'America/Chicago';

/** `mediumDate`: Oct 15, 2026. `numeric`: 10/15/2026. */
export type CalendarDateStyle = 'mediumDate' | 'numeric';

export type DateTimeInput = Date | string | null | undefined;

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const CALENDAR_PREFIX = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/;
const ZONED_SUFFIX = /(?:Z|[+-]\d{2}:?\d{2})$/i;
const CLOCK_PARTS =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3})\d*)?)?)?$/;

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const zonedFormatterCache = new Map<string, Intl.DateTimeFormat>();

/**
 * The viewer's IANA time zone (e.g. `America/Chicago`), read from the browser
 * through `Intl`. A user profile setting can replace this later; every caller
 * goes through here, so that is a one-place change.
 */
export function resolveUserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function zonedFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = zonedFormatterCache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hourCycle: 'h23',
    });
    zonedFormatterCache.set(timeZone, formatter);
  }
  return formatter;
}

function toZonedParts(instant: Date, timeZone: string): ZonedParts {
  const parts: Record<string, number> = {};
  for (const part of zonedFormatter(timeZone).formatToParts(instant)) {
    if (part.type !== 'literal') {
      parts[part.type] = Number(part.value);
    }
  }
  return {
    year: parts['year'],
    month: parts['month'],
    day: parts['day'],
    // Some engines print midnight as 24 under h23; normalise it.
    hour: parts['hour'] % 24,
    minute: parts['minute'],
    second: parts['second'],
  };
}

const pad2 = (n: number): string => n.toString().padStart(2, '0');

function clock(p: ZonedParts, withSeconds: boolean): string {
  const hour12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  const meridiem = p.hour < 12 ? 'AM' : 'PM';
  const seconds = withSeconds ? `:${pad2(p.second)}` : '';
  return `${hour12}:${pad2(p.minute)}${seconds} ${meridiem}`;
}

/**
 * Formats an instant in the viewer's time zone (or `timeZone` when given).
 * Returns `''` for a missing or unreadable value, so templates need no guard.
 */
export function formatUserDateTime(
  value: DateTimeInput,
  style: UserDateTimeStyle = 'short',
  timeZone: string = resolveUserTimeZone()
): string {
  const instant = coerceToDate(value);
  if (!instant) return '';
  const p = toZonedParts(instant, timeZone);
  switch (style) {
    case 'medium':
      return `${MONTHS_SHORT[p.month - 1]} ${p.day}, ${p.year}, ${clock(p, true)}`;
    case 'numeric':
      return `${p.month}/${pad2(p.day)}/${p.year} ${clock(p, false)}`;
    case 'numericDate':
      return `${p.month}/${pad2(p.day)}/${p.year}`;
    default:
      return `${p.month}/${p.day}/${pad2(p.year % 100)}, ${clock(p, false)}`;
  }
}

/**
 * The instant a zoneless UTC clock reading stands for.
 *
 * Some legacy columns are stamped from `DateTime.UtcNow` but reach the browser
 * with no `Z` (Sparta `DateExported`, Bug 37590), so the date reviver reads the
 * UTC clock as local time. A `Date` is taken back to its local clock components
 * and read as UTC; a string with no zone is read as UTC. A string that already
 * carries `Z` or an offset is an exact instant and is not shifted again.
 * Returns `null` for a missing or unreadable value.
 */
export function fromUtcClock(value: DateTimeInput): Date | null {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(
      Date.UTC(
        value.getFullYear(),
        value.getMonth(),
        value.getDate(),
        value.getHours(),
        value.getMinutes(),
        value.getSeconds(),
        value.getMilliseconds()
      )
    );
  }
  const text = value?.trim();
  if (!text) return null;
  if (ZONED_SUFFIX.test(text)) return coerceToDate(text);
  const match = CLOCK_PARTS.exec(text);
  if (!match) return null;
  const [, y, mo, d, h = '0', mi = '0', sec = '0', ms = '0'] = match;
  const instant = new Date(
    Date.UTC(
      Number(y),
      Number(mo) - 1,
      Number(d),
      Number(h),
      Number(mi),
      Number(sec),
      Number(ms.padEnd(3, '0'))
    )
  );
  return Number.isNaN(instant.getTime()) ? null : instant;
}

/**
 * The `yyyy-MM-dd` calendar day of a date-only value, with no time zone shift.
 *
 * A string uses its own `yyyy-MM-dd` prefix (so `2026-10-15`,
 * `2026-10-15T00:00:00` and `2026-10-15T00:00:00Z` are all Oct 15). A `Date`
 * uses its local calendar components, because the date reviver turns a no-Z
 * wire midnight into local midnight. Never `toISOString()`, which is the UTC
 * day and lands on the previous day for any zone east of UTC.
 */
export function toCalendarDateIso(value: DateTimeInput): string | null {
  if (typeof value === 'string') {
    const match = CALENDAR_PREFIX.exec(value.trim());
    if (match) {
      const [, y, m, d] = match;
      const check = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
      return check.getUTCMonth() === Number(m) - 1 &&
        check.getUTCDate() === Number(d)
        ? `${y}-${m}-${d}`
        : null;
    }
  }
  const date = coerceToDate(value);
  return date ? formatDateAsIsoLocal(date) : null;
}

/**
 * Formats a calendar date (no time) with no time zone shift.
 * Returns `''` for a missing or unreadable value.
 */
export function formatCalendarDate(
  value: DateTimeInput,
  style: CalendarDateStyle = 'mediumDate'
): string {
  const iso = toCalendarDateIso(value);
  if (!iso) return '';
  const [year, month, day] = iso.split('-').map(Number);
  return style === 'numeric'
    ? `${month}/${pad2(day)}/${year}`
    : `${MONTHS_SHORT[month - 1]} ${day}, ${year}`;
}
